import { NetworkStatus, useQuery } from "@apollo/client";
import {
  AccountCard,
  Button,
  CurrencyAmountUI,
  DataTable,
  DataTableColumnHeader,
  DateUI,
  externalAccountDetailsTypenameToDataSource,
  PrimaryFullScreenSpinner,
  StatusUI,
} from "@repo/ui";
import { createFileRoute, Link } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { DateTime } from "luxon";
import { Suspense } from "react";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { graphql } from "~/gql/client";
import type { AssetCashflowReportEntryFieldsFragment } from "~/gql/client/graphql";
import { opsHead } from "~/head";

const PAGE_SIZE = 50;

const assetCashflowReportsDocument = graphql(`
  query AdminAssetCashflowReports($first: Int!, $after: String) {
    assetCashflowReports(first: $first, after: $after) {
      totalCount
      pageInfo {
        hasNextPage
        endCursor
      }
      items {
        ...AssetCashflowReportEntryFields
      }
    }
  }

  fragment AssetCashflowReportEntryFields on AssetCashflowReportEntry {
    asset {
      ...AssetCardFields
    }
    periodStart
    periodEnd
    values {
      opening {
        amount
        currency
      }
      net {
        amount
        currency
      }
      unattributed {
        amount
        currency
      }
      closing {
        amount
        currency
      }
    }
  }
`);

export const Route = createFileRoute("/__auth/cashflow-reports")({
  head: () => opsHead("Cashflow Reports Debug"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

// Full month/year/financial-year periods get a short label; anything else
// (or an open "to current" period) falls back to a date range / *-to-date.
function formatPeriodLabel(
  periodStart: Date,
  periodEnd: Date | null,
): string | null {
  const start = DateTime.fromJSDate(periodStart);

  if (!periodEnd) {
    const now = DateTime.now();
    return start.hasSame(now, "month") ? "Month to date" : "Year to date";
  }

  const end = DateTime.fromJSDate(periodEnd);
  const dayAfterEnd = end.plus({ days: 1 }).toISODate();

  if (
    start.day === 1 &&
    dayAfterEnd === start.plus({ months: 1 }).toISODate()
  ) {
    return start.toFormat("MMM yyyy");
  }

  if (start.day === 1 && dayAfterEnd === start.plus({ years: 1 }).toISODate()) {
    const startYy = String(start.year % 100).padStart(2, "0");
    const endYy = String(end.year % 100).padStart(2, "0");
    return start.month === 1 ? String(start.year) : `FY${startYy}/${endYy}`;
  }

  return null;
}

const columns: ColumnDef<AssetCashflowReportEntryFieldsFragment>[] = [
  {
    id: "asset",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Account" />
    ),
    cell: ({ row }) => {
      const { asset } = row.original;
      return (
        <div className="flex items-center gap-2">
          <span className="text-tertiary text-xs">#{row.index + 1}</span>
          <AccountCard
            account={asset}
            variant="DEFAULT"
            subText={["CURRENCY", "BUSINESS_ENTITY"]}
            linkTo={`/assets/${asset.id}`}
          />
          <div className="flex flex-col items-end gap-1">
            {asset.assetStatus !== "ACTIVE" && (
              <StatusUI status={asset.assetStatus} />
            )}
            {asset.__typename === "ExternalBankAccountAsset" &&
              asset.details && (
                <span className="text-2xs text-tertiary">
                  {
                    externalAccountDetailsTypenameToDataSource[
                      asset.details.__typename
                    ]
                  }
                </span>
              )}
          </div>
        </div>
      );
    },
  },
  {
    id: "period",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Period" />
    ),
    cell: ({ row }) => {
      const label = formatPeriodLabel(
        row.original.periodStart,
        row.original.periodEnd ?? null,
      );
      if (label) {
        return <div>{label}</div>;
      }
      return (
        <span className="flex items-center gap-1">
          <DateUI date={row.original.periodStart} variant="DATE" />
          <span>-</span>
          {row.original.periodEnd ? (
            <DateUI date={row.original.periodEnd} variant="DATE" />
          ) : (
            "today"
          )}
        </span>
      );
    },
  },
  {
    id: "opening",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Opening" align="right" />
    ),
    cell: ({ row }) => (
      <div className="text-right">
        <CurrencyAmountUI ca={row.original.values.opening} hideCurrency />
      </div>
    ),
  },
  {
    id: "net",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Net" align="right" />
    ),
    cell: ({ row }) => (
      <div className="text-right">
        <CurrencyAmountUI ca={row.original.values.net} hideCurrency />
      </div>
    ),
  },
  {
    id: "closing",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Closing" align="right" />
    ),
    cell: ({ row }) => (
      <div className="text-right">
        <CurrencyAmountUI ca={row.original.values.closing} hideCurrency />
      </div>
    ),
  },
  {
    id: "unattributed",
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title="Unattributed"
        align="right"
      />
    ),
    cell: ({ row }) => (
      <div className="text-right">
        <CurrencyAmountUI ca={row.original.values.unattributed} hideCurrency />
      </div>
    ),
  },
];

function View() {
  const { data, loading, networkStatus, fetchMore } = useQuery(
    assetCashflowReportsDocument,
    {
      variables: { first: PAGE_SIZE, after: null },
      notifyOnNetworkStatusChange: true,
    },
  );

  const items = data?.assetCashflowReports.items ?? [];
  const pageInfo = data?.assetCashflowReports.pageInfo;

  return (
    <AdminPageContainer fullWidth testId="admin-cashflow-reports-debug">
      <AdminPageHeader
        title="Cashflow Reports Debug"
        subtitle="All cached per-asset cashflow report entries, sorted by unattributed amount"
        rightContent={
          <Button asChild variant="outline">
            <Link to="/cashflow-reports/debug">Browse</Link>
          </Button>
        }
      />
      <DataTable
        data={items}
        columns={columns}
        isLoadingData={loading && networkStatus !== NetworkStatus.fetchMore}
        manualPagination
        hidePagination
        pageSize={PAGE_SIZE}
        emptyState={<>No cashflow report entries found.</>}
        fixedLayoutPcWidths={{
          asset: 37,
          period: 13,
          opening: 12.5,
          net: 12.5,
          closing: 12.5,
          unattributed: 12.5,
        }}
      />
      {pageInfo?.hasNextPage && (
        <div className="flex justify-center pt-4">
          <Button
            variant="outline"
            onClick={() =>
              void fetchMore({ variables: { after: pageInfo.endCursor } })
            }
            loading={networkStatus === NetworkStatus.fetchMore}
          >
            Load more
          </Button>
        </div>
      )}
    </AdminPageContainer>
  );
}
